import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import eventPoster from '../../assets/images/eventPoster.jpg';
import {ReactComponent as EndDateSVG} from '../../assets/svgs/calendar-end-date.svg'
import {ReactComponent as StartDateSVG} from '../../assets/svgs/calendar-start-date.svg'
import {ReactComponent as CategorySVG} from '../../assets/svgs/folder.svg'
import {ReactComponent as LocationSVG} from '../../assets/svgs/location.svg'
import {ReactComponent as EmailSVG} from '../../assets/svgs/email.svg'
import {ReactComponent as PhoneSVG} from '../../assets/svgs/phone.svg'
import {ReactComponent as ProviderSVG} from '../../assets/svgs/provider.svg'
import {ReactComponent as SiteLinkSVG} from '../../assets/svgs/sitelink.svg'
import {ReactComponent as PhotoSVG} from '../../assets/svgs/photo.svg'
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import PhotoGallery from '../../components/PhotoGallery'
import MapModal from '../MapModal';
import 'leaflet/dist/leaflet.css';

const EventDetailsContainer = styled.div`
  padding: 50px;
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  word-wrap: break-word;
  text-overflow: ellipsis;
  display: flex;
  flex-direction: ${({ hasPoster }) => (hasPoster ? 'row' : 'column')};
  align-items: ${({ hasPoster }) => (hasPoster ? 'flex-start' : 'center')};
  justify-content: center;
  gap: 50px;
  text-align: ${({ hasPoster }) => (hasPoster ? 'left' : 'center')};

  width: fit-content;
  max-width: 90vw; /* Keeps it responsive */
  min-width: 300px; /* Prevents it from shrinking too much */
  margin: 50px auto; /* Centers it horizontally */

  @media (max-width: 768px) { 
    flex-direction: column;
    align-items:center;
    width: 100%; /* Use full width on mobile */
    max-width: 90%; /* Keep some margin on the sides */
  }

  /* Landscape tablet (768px to 1024px) */
@media (min-width: 768px) and (max-width: 1024px) {
  flex-direction: column;
    align-items:center;
    width: 100%; /* Use full width on mobile */
    max-width: 90%; /* Keep some margin on the sides */
}
`;

const EventPoster = styled.img`
  max-width: 300px;
  height: auto;
  border-radius: 10px;
  place-self:center;
`;


const EventInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: ${({ hasPoster }) => (hasPoster ? 'flex-start' : 'center')};
  text-align: ${({ hasPoster }) => (hasPoster ? 'left' : 'center')};

  @media (max-width: 768px) { 
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 14px; /* Adjust font size for mobile */
  }

  @media (min-width: 768px) and (max-width: 1024px) {
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 15px; /* Adjust font size for tablets */
  }
  
  /* Desktop (default) */
  font-size: 16px; /* Default font size for desktop */
`;



const EventDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr; 
  gap: 20px; /* Increased spacing for better readability */
  width: 100%;
  margin-top: 10px;

  @media (min-width: 768px) { 
    grid-template-columns: 1fr 1fr;
  }

   @media (max-width: 768px) { 
     font-size:12px;  
  }

  p {
    font-size: 16px;
    display: flex;
    flex-direction: column; /* Stack label and value */
    gap: 5px; /* Spacing between label and value */
  }

  strong {
    font-weight: bold;
    color: #385963;
  }
`;

const Button = styled.button`
  background-color: white;
  color: #385963;
  font-weight: 400;
  border: #385963 solid 2px;
  padding: 10px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
  margin-top: 10px;
  width: ${({ selectedLanguage }) => (selectedLanguage === 'gr' ? '240px' : '170px')};
  height: auto;
 
  &:hover {
    color: white;
    transition: 0.4s;
    cursor: pointer;
    background-color: #385963;
  }

`;


const FullscreenModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 30px;
  background: none;
  color: white;
  border: none;
  font-size: 28px;
  cursor: pointer;
  z-index: 10000;
`;

const EventPhotoLink = styled.div`
  cursor: pointer;
  display:flex;
  place-self:center;

  strong {
    text-decoration: underline;
    transition: 0.4s;
    cursor: pointer;
    color: #385963;
     font-size: 16px;
    display: flex;
    flex-direction: column; /* Stack label and value */
    gap: 5px; /* Spacing between label and value */
  }

  &:hover strong {
    color: #0056b3;
  }

  @media (max-width: 768px) { 
    align-items:center;
    justify-content:center;
  }

  
  @media (min-width: 768px) and (max-width: 1024px) {
    align-items:center;
    justify-content:center;
  }
`;


// FilePlaceTypeEnum
const FILE_PLACE_TYPE = {
  Gallery: 0,
  CategoryPhoto: 1,
  CoverPhoto: 2,
  Poster: 3,
};

const EventDetails = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const selectedLanguage = sessionStorage.getItem('selectedLanguage') || 'en';
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const location = useLocation();
  const { state } = location;
  const [event, setEvent] = useState({});
  const [eventId, setEventId] = useState(state?.eventId || '');
  const tokenString = sessionStorage.getItem("token");
  const [showGallery, setShowGallery] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState([]);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);

  useEffect(() => {
    if (state?.eventId) {
      setEventId(state.eventId);
    }
  }, [state]);

     // Initialize form with default structure to avoid undefined errors
      const [form, setForm] = useState({
        name: { gr: '', en: '' },
        description: { gr: '', en: '' },
        location: { latitude: '', longitude: '' },
        link360View: '',
        panoramicView: '',
        categoryId: '',
        coverPhoto: null,
        categoryPhoto: null,
        photos: [],
        existingCoverFileName: null,
        existingCategoryFileName: null,
        // Add existing photos filenames if needed to handle delete on server
        existingGalleryFileNames: [], // optional
        posterPhoto: null,
        existingPosterFileName: null,
        posterBase64Src: null,
      });

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`${BASE_URL}Event/${eventId}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setEvent(data);
        const rawFileNames = data.fileNames; 
        let fetchedFiles = [];
  
        if (rawFileNames.length > 0) {
          const fileResponse = await fetch(`${BASE_URL}Event/getFiles`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${tokenString}`,
            },
            body: JSON.stringify( rawFileNames),
          });
  
          if (!fileResponse.ok) throw new Error("Failed to fetch file contents");
          fetchedFiles = await fileResponse.json();
        }
  
        // Process fetched files based on their filePlaceType
        const {
          existingCoverFileName,
          existingCategoryFileName,
          existingGalleryFileNames, 
          existingPosterFileName,
          galleryBase64Srcs,
          coverBase64Src,
          categoryBase64Src,
          posterBase64Src, 
        } = processFiles(fetchedFiles);
  
        setForm({
          name: data.name || { gr: '', en: '' },
          description: data.description || { gr: '', en: '' },
          location: data.location || { latitude: '', longitude: '' },
          link360View: data.link360View || '',
          panoramicView: data.panoramicView || '',
          categoryId: data.categoryId || '',
          coverPhoto: null,
          categoryPhoto: null,
          photos: [],
          existingCoverFileName,
          existingCategoryFileName,
           existingGalleryFileNames,
          galleryBase64Srcs,
          coverBase64Src,
          categoryBase64Src,
         posterPhoto: null,
         existingPosterFileName,
         posterBase64Src,
        });
 
        
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (eventId) {
      fetchEvent();
    }
  }, [eventId]);

    const processFiles = (files) => {
  const coverFile = files.find(f => f.filePlaceType === FILE_PLACE_TYPE.CoverPhoto);
  const categoryFile = files.find(f => f.filePlaceType === FILE_PLACE_TYPE.CategoryPhoto);
  const galleryFiles = files.filter(f => f.filePlaceType === FILE_PLACE_TYPE.Gallery);
  const posterFile = files.find(f => f.filePlaceType === FILE_PLACE_TYPE.Poster); 

  const coverBase64Src = coverFile ? `data:${coverFile.mimeType};base64,${coverFile.base64Content}` : null;
  const categoryBase64Src = categoryFile ? `data:${categoryFile.mimeType};base64,${categoryFile.base64Content}` : null;
  const galleryBase64Srcs = galleryFiles.map(f => `data:${f.mimeType};base64,${f.base64Content}`);
    const posterBase64Src = posterFile ? `data:${posterFile.mimeType};base64,${posterFile.base64Content}` : null; 

  // Collect filenames of gallery files to handle deletions later
  const existingGalleryFileNames = galleryFiles.map(f => f.fileName);


  return {
    existingCoverFileName: coverFile?.fileName || null,
    existingCategoryFileName: categoryFile?.fileName || null,
    existingPosterFileName: posterFile?.fileName || null, 
    existingGalleryFileNames,  
    galleryBase64Srcs,
    coverBase64Src,
    categoryBase64Src,
    posterBase64Src,
  };
};

  let eventStart = new Date(event.startDate);
  let eventEnd = new Date(event.endDate);

    // Format the start date
  let formattedStart = eventStart.toLocaleString(selectedLanguage, {
      weekday: 'long',  // Full day name
      year: 'numeric',  // Year with four digits
      month: 'long',    // Full month name
      day: 'numeric',   // Day of the month
      hour: '2-digit',  // Two-digit hour
      minute: '2-digit', // Two-digit minute
      hour12: true      // 12-hour clock with AM/PM
    });

    // Format the end date
  let formattedEnd = eventEnd.toLocaleString(selectedLanguage, {
      weekday: 'long',  // Full day name
      year: 'numeric',  // Year with four digits
      month: 'long',    // Full month name
      day: 'numeric',   // Day of the month
      hour: '2-digit',  // Two-digit hour
      minute: '2-digit', // Two-digit minute
      hour12: true      // 12-hour clock with AM/PM
    });

  const handleEventNavigation = () => {
    navigate('/event-calendar', { state: { activeComponent: 'calendar' } });
  };

  const hasPoster = !!form.posterBase64Src;

  return (
    <EventDetailsContainer hasPoster={hasPoster} selectedLanguage={selectedLanguage}>
      {hasPoster && <EventPoster src={form.posterBase64Src} alt="Event Poster" />}

      <EventInfo hasPoster={hasPoster}>
        <h2>{event.name?.[selectedLanguage]}</h2>
        <p><strong>{i18n.t('description')}:</strong> <div style={{ whiteSpace: "pre-line" }}>{event.description?.[selectedLanguage]}</div></p>

        <EventDetailsGrid>
          <p><strong> <StartDateSVG  height={25} width={25} style={{ marginRight: '10px' }} /> {i18n.t('startDate')}</strong> {formattedStart}</p>
          <p><strong><EndDateSVG  height={25} width={25} style={{ marginRight: '10px' }} /> {i18n.t('endDate')}</strong> {formattedEnd}</p>
          <p><strong><CategorySVG  height={25} width={26} style={{ marginRight: '10px' }} /> {i18n.t('category')}</strong> {event.categoryName?.[selectedLanguage]}</p>
          {/* <p><strong><LocationSVG  height={25} width={26} style={{ marginRight: '10px' }} /> {i18n.t('location')}</strong> {event.location?.latitude}, {event.location?.longitude}</p> */}
           <p style={{ cursor: 'pointer'}} 
             onClick={() => {
               setSelectedPosition([event.location?.latitude, event.location?.longitude]);
               setShowMapModal(true);
             }}><strong>{i18n.t('location')}:</strong>
             <div style={{ textDecoration:'underline' , color: '#007bff'}}>{event.location?.longitude}, {event.location?.latitude}</div>
           </p>
          <p>
            <>
            <strong><EmailSVG  height={25} width={26} style={{ marginRight: '10px' }} /> {i18n.t('email')}</strong>
            <a href={`mailto:${event.email}`} style={{ color: '#007bff', textDecoration: 'underline' }}>
              {event.email}
            </a>
            </>
          </p>
          <p><strong><PhoneSVG  height={25} width={26} style={{ marginRight: '10px' }} />{i18n.t('phone')}</strong> {event.phone}</p>
          <p><strong><ProviderSVG  height={25} width={26} style={{ marginRight: '10px' }} />{i18n.t('providerName')}</strong> {event.providerName}</p>
          <p>
            <strong><SiteLinkSVG  height={25} width={26} style={{ marginRight: '10px' }} />{i18n.t('link')}</strong>
            <a href={event.siteLink} target="_blank" rel="noopener noreferrer">{event.siteLink}</a>
          </p>

         
        </EventDetailsGrid>

        {showGallery && (
          <FullscreenModal>
            <CloseButton onClick={() => setShowGallery(false)}>x</CloseButton>
            <PhotoGallery photos={selectedGallery} />
          </FullscreenModal>
        )}

         {showMapModal && selectedPosition && (
          <MapModal
            position={selectedPosition}
            onClose={() => setShowMapModal(false)}
          />
        )}
       
      </EventInfo>
       {form.galleryBase64Srcs?.length > 0 && (
            <EventPhotoLink 
               onClick={() => {
                  setSelectedGallery(form.galleryBase64Srcs || []);
                  setShowGallery(true);
                }}
            >
              <PhotoSVG  height={25} width={26} style={{ marginRight: '10px' }} />
               <strong>{i18n.t('photos')}</strong>
            </EventPhotoLink>
          )}

      <Button onClick={handleEventNavigation}>{i18n.t('close')}</Button>
    </EventDetailsContainer>
  );
};

export default EventDetails;



